/**
 * Helpers for the time-on-system screen.
 *
 * The backend reports minutes estimated from activity in fixed slots, not
 * measured with a stopwatch. These helpers keep the presentation honest about
 * that: durations round to whole minutes, and an absent user reads as an em
 * dash rather than "0 h 00 m", which looks like a measurement rather than an
 * absence.
 */

const IST_ZONE = 'Asia/Kolkata';

/** "6 h 12 m", "48 m", "—" for nothing. */
export function formatWorkedDuration(minutes) {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return '—';
  const hours = Math.floor(value / 60);
  const mins = Math.round(value % 60);
  if (!hours) return `${mins} m`;
  if (!mins) return `${hours} h`;
  return `${hours} h ${String(mins).padStart(2, '0')} m`;
}

/** Decimal hours for the summary tiles, e.g. 6.2. */
export function toHours(minutes) {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.round((value / 60) * 10) / 10;
}

/** "09:12" in IST, or "—" when the user was never seen. */
export function formatClockIst(value) {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: IST_ZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
}

/** Today as YYYY-MM-DD in IST — the day boundary the backend reports on. */
export function todayIst() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
  return parts;
}

/** N days before today, as YYYY-MM-DD in IST. */
export function daysAgoIst(days) {
  const date = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(date);
}

/**
 * Expand the sparse buckets the API returns into a full grid for one day, so
 * the strip shows gaps as gaps rather than silently closing them up.
 *
 * `windowStartHour`/`windowEndHour` trim the strip to working hours; slots
 * outside the window are dropped, but a bucket inside a trimmed hour is not
 * lost — it just is not drawn, which is why the caller shows the totals too.
 */
export function buildDayStrip(
  buckets,
  { day, bucketMinutes = 5, windowStartHour = 6, windowEndHour = 23 } = {}
) {
  if (!day) return [];

  const width = bucketMinutes * 60 * 1000;
  const dayStart = new Date(`${day}T00:00:00+05:30`).getTime();
  if (Number.isNaN(dayStart)) return [];

  const active = new Map();
  for (const bucket of buckets || []) {
    const start = new Date(bucket.bucket_start).getTime();
    if (Number.isNaN(start)) continue;
    active.set(start, Number(bucket.request_count) || 0);
  }

  const slots = [];
  const from = dayStart + windowStartHour * 60 * 60 * 1000;
  const to = dayStart + windowEndHour * 60 * 60 * 1000;
  for (let t = from; t < to; t += width) {
    // Buckets are aligned to the epoch grid, not to midnight IST, so a slot's
    // key is the grid cell it falls in rather than the loop counter.
    const key = Math.floor(t / width) * width;
    slots.push({
      startMs: key,
      label: formatClockIst(new Date(key)),
      active: active.has(key),
      requests: active.get(key) || 0
    });
  }
  return slots;
}

/**
 * Roles that represent people operating the product.
 *
 * The user table also holds CRM and customer records — leads especially, which
 * carry vertical "Management" and designation "Admin" and so are impossible to
 * tell from a real admin without the role. On UAT 371 of 609 roster rows were
 * leads, which is why this filter exists rather than trusting the list as sent.
 *
 * An allow-list, so a role nobody has thought about is absent from a usage
 * report by default rather than silently padding it.
 */
export const STAFF_ROLES = ['builder', 'admin', 'sales-admin', 'sales'];

/** True when this row is a person who operates Housingram. */
export function isStaffRow(user) {
  const role = String(user?.role || '').trim().toLowerCase();
  // A row with no role at all is kept: better to show a real person we cannot
  // classify than to hide them and understate the team.
  if (!role) return true;
  return STAFF_ROLES.includes(role);
}

/** Split a roster into the people who operate the product and everyone else. */
export function partitionStaff(users) {
  const staff = [];
  const nonStaff = [];
  for (const user of users || []) {
    (isStaffRow(user) ? staff : nonStaff).push(user);
  }
  return { staff, nonStaff };
}

/** Headline figures computed from whatever rows are actually on screen. */
export function summariseUsers(users) {
  const rows = users || [];
  const active = rows.filter((u) => Number(u.active_minutes) > 0);
  const totalMinutes = rows.reduce((sum, u) => sum + (Number(u.active_minutes) || 0), 0);
  return {
    totalUsers: rows.length,
    activeUsers: active.length,
    totalMinutes,
    avgMinutes: active.length ? Math.round(totalMinutes / active.length) : 0
  };
}

/**
 * Group users under the company they belong to.
 *
 * Builder-owner logins belong to no company and come back with a null, so they
 * are collected under their own heading rather than dropped — they are usually
 * the account that matters most.
 *
 * Companies are ordered by usage so the ones that have stopped using the product
 * sit together at the bottom, and users keep the order the API sent them
 * (most active first) inside each group.
 */
export function groupUsersByCompany(users, { hideInactive = false } = {}) {
  const source = hideInactive
    ? (users || []).filter((u) => Number(u.active_minutes) > 0)
    : users || [];

  const byCompany = new Map();
  for (const user of source) {
    const key = user.company_id == null ? 'none' : String(user.company_id);
    let group = byCompany.get(key);
    if (!group) {
      group = {
        key,
        companyId: user.company_id ?? null,
        companyName: user.company_name || 'No company',
        users: [],
        activeUsers: 0,
        totalUsers: 0,
        totalMinutes: 0
      };
      byCompany.set(key, group);
    }
    const minutes = Number(user.active_minutes) || 0;
    group.users.push(user);
    group.totalUsers += 1;
    group.totalMinutes += minutes;
    if (minutes > 0) group.activeUsers += 1;
  }

  return Array.from(byCompany.values()).sort((a, b) => {
    if (b.totalMinutes !== a.totalMinutes) return b.totalMinutes - a.totalMinutes;
    return a.companyName.localeCompare(b.companyName);
  });
}

/**
 * Share of a group who opened Housingram at all, as a whole percent.
 *
 * Kept alongside the duration helpers so the screen and the PDF report agree on
 * what "adoption" means — the report computes the same thing server-side.
 */
export function adoptionPct(active, total) {
  const a = Number(active) || 0;
  const t = Number(total) || 0;
  if (t <= 0) return 0;
  return Math.round((a / t) * 100);
}

/**
 * Longest custom range the page will ask for. Past a year the per-user table
 * stops being readable and the PDF gets long; the export covers bigger pulls.
 */
export const MAX_CUSTOM_RANGE_DAYS = 366;

/** Whole days in an inclusive YYYY-MM-DD range, or 0 when it is not a range. */
export function daysInRange(from, to) {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b) || b < a) return 0;
  return Math.round((b - a) / 86400000) + 1;
}

/**
 * Check a custom date range picked on the page. Returns a message the page can
 * show as-is, or null when the range is usable — so the fetch never fires with
 * a range the server would have to guess about.
 */
export function validateCustomRange(from, to, today = todayIst()) {
  const shape = /^\d{4}-\d{2}-\d{2}$/;
  if (!from || !to) return 'Pick both a start and an end date.';
  if (!shape.test(from) || !shape.test(to)) return 'Dates must be in YYYY-MM-DD form.';
  if (from > to) return 'The start date is after the end date.';
  if (to > today) return 'The end date is in the future.';
  if (daysInRange(from, to) > MAX_CUSTOM_RANGE_DAYS) {
    return 'Pick a range of a year or less, or use Export CSV for longer.';
  }
  return null;
}

/** "10 Sep, 14:32" in IST, for a line of login history. */
export function formatDateTimeIst(value) {
  if (!value) return '—';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: IST_ZONE,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
}

/** "15 Sep 2026" from a YYYY-MM-DD day key, without re-interpreting the zone. */
export function formatDayIst(day) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(day || ''))) return String(day || '');
  // Parsed as midnight IST so the calendar day the backend named is the one
  // rendered — `new Date('2026-09-15')` is UTC midnight, which is the day
  // before in every zone west of Greenwich.
  const date = new Date(`${day}T00:00:00+05:30`);
  if (Number.isNaN(date.getTime())) return String(day);
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: IST_ZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  }).format(date);
}

/**
 * How much of a range predates time-on-system tracking, or null when all of it
 * is covered.
 *
 * Minutes exist only from the day tracking was switched on and cannot be
 * backfilled, but Logins come from the session table and Views and Clicks from
 * Mixpanel — both of which have older history. Widen the dates past the start
 * of tracking and those three columns move while Time and Days sit still, which
 * reads as a date filter that does not work. Nothing on the page can tell those
 * two apart, so the page has to say which one it is.
 */
export function describeCoverageGap(range, coverage) {
  const firstDay = coverage?.first_day;
  if (!range?.from || !range?.to || !firstDay) return null;
  if (firstDay <= range.from) return null;

  const totalDays = daysInRange(range.from, range.to);
  if (!totalDays) return null;

  // A range that ends before tracking started has no covered days at all.
  const coveredDays = firstDay > range.to ? 0 : daysInRange(firstDay, range.to);
  return {
    firstDay,
    totalDays,
    coveredDays,
    missingDays: totalDays - coveredDays
  };
}

/** Which state the feature panel is in. */
export function featurePanelState(data) {
  if (!data) return 'hidden';
  if (data.available === false) return 'unavailable';
  if (!(data.features || []).length) return 'empty';
  return 'ready';
}

/**
 * Which state the page-views-and-clicks panel is in. Each reason it can be empty
 * gets its own state, so "Mixpanel is not connected" never reads the same as
 * "nobody clicked anything".
 */
export function interactionPanelState(data) {
  if (!data) return 'hidden';
  if (data.available === false) return 'unavailable';
  if (!data.mixpanel?.configured || !data.mixpanel?.enabled) return 'not_connected';
  if (!data.days_synced) return 'waiting';
  return 'ready';
}
